import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
  feedbackCorrect,
  feedbackIncorrect,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { GEAR_SPEED_ONE_SHOT } from './shared/engineInteractionOneShots';
import { isTapFirstLessonUi } from './shared/lessonUiMode';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';
import GearSvg from './shared/GearSvg';

/**
 * Gear speed / ratio reasoning on a connected train.
 *
 * Content:
 * {
 *   instructions?, prompt?,
 *   gears: [{ id, x, y, radius, teeth?, label? }],
 *   connections: [{ from, to }],
 *   questionMode: 'fastest' | 'slowest' | 'turn_count',
 *   answer: string (gearId) for fastest/slowest,
 *   turnCount?: { referenceGearId, targetGearId, referenceTurns?: number },
 *   answerTurns?: number (required when questionMode === 'turn_count'),
 *   scene?: { width, height },
 *   turnChoices?: number[],
 *   showTeethLabels?: boolean,   // default: true when any gear has explicit `teeth`
 *   showTeethSummary?: boolean, // default: true for turn_count when ≥2 gears have `teeth` — one-line legend under prompt
 *   victory?, explanation?,
 * }
 *
 * Guided shell (`onRegisterLessonFooterActions`): Check uses the lesson footer; in-engine Check is hidden.
 * For `fastest` / `slowest` in the shell, the train stays still until **Check** (then spins), so picks are not
 * given away by motion. `turn_count` keeps the train spinning for visual reference.
 */

function buildAdjacency(connections) {
  const adj = new Map();
  const add = (a, b) => {
    if (!adj.has(a)) adj.set(a, []);
    if (!adj.has(b)) adj.set(b, []);
    adj.get(a).push(b);
    adj.get(b).push(a);
  };
  for (const e of connections || []) {
    if (e?.from && e?.to) add(e.from, e.to);
  }
  return adj;
}

function gearRadius(g) {
  return Math.max(12, Number(g.radius) || 32);
}

function teethOrRadius(g) {
  if (g.teeth != null && Number.isFinite(Number(g.teeth))) return Number(g.teeth);
  return gearRadius(g);
}

/** Authoritative tooth count from JSON, or null if not given (do not invent counts for kids). */
function explicitTeeth(g) {
  if (g?.teeth == null) return null;
  const n = Number(g.teeth);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n);
}

/**
 * Propagate angular velocities: ω_b = -ω_a * (T_a / T_b) for external mesh.
 */
function computeAngularSpeeds(gears, connections, rootId) {
  const adj = buildAdjacency(connections);
  const omega = new Map();
  if (!rootId || !adj.has(rootId)) return omega;
  omega.set(rootId, 1);
  const q = [rootId];
  while (q.length) {
    const u = q.shift();
    const wu = omega.get(u);
    const Tu = teethOrRadius(gears.find((g) => g.id === u) || {});
    for (const v of adj.get(u) || []) {
      if (omega.has(v)) continue;
      const Tv = teethOrRadius(gears.find((g) => g.id === v) || {});
      const wv = (-wu * Tu) / Math.max(1e-6, Tv);
      omega.set(v, wv);
      q.push(v);
    }
  }
  return omega;
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

export default function GearSpeedEngine({
  title = 'Gear speed',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_gear_speed',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  lessonUiMode = 'default',
  guidedContext,
  onRegisterLessonFooterActions,
}) {
  const singleSurface = guidedContext?.singleSurface === true;
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const suppressActivityTitle = shouldSuppressActivityTitle({ stepTitle, activityTitle: title });
  const tapFirst = isTapFirstLessonUi(lessonUiMode);
  const compactGear = tapFirst && singleSurface;
  const shellLesson = typeof onRegisterLessonFooterActions === 'function';

  const gears = Array.isArray(content.gears) ? content.gears : [];
  const connections = content.connections || [];
  const questionMode = content.questionMode || 'fastest';
  const sceneW = Number(content.scene?.width) || 320;
  const sceneH = Number(content.scene?.height) || 220;
  const rootId = content.referenceGearId || gears[0]?.id;

  const omega = useMemo(
    () => computeAngularSpeeds(gears, connections, rootId),
    [gears, connections, rootId],
  );

  const [pickedGearId, setPickedGearId] = useState(null);
  const [pickedTurns, setPickedTurns] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [showCelebration, celebrate] = useCelebration();
  const [a11y, setA11y] = useState('');
  const lastTickRef = useRef(0);
  const gearRefs = useRef({});

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint: GEAR_SPEED_ONE_SHOT,
    explanation,
    getExplanation,
    isActivityComplete: completed,
    enabled: guidedHintsEnabled,
  });
  const { noteUserAction, noteIncorrectAnswer, noteCorrectAnswer } = guided;
  const narrowGear = compactGear && guided.isNarrow;

  const deferSpinUntilCheck =
    shellLesson && (questionMode === 'fastest' || questionMode === 'slowest');
  const spinAllowedRef = useRef(!deferSpinUntilCheck);

  const snapGearAngles = useCallback(() => {
    gears.forEach((g) => {
      const el = gearRefs.current[g.id];
      if (el) {
        el.dataset.angle = '0';
        el.style.transform = 'rotate(0deg)';
      }
    });
  }, [gears]);

  const gearsSig = useMemo(() => gears.map((g) => g.id).join('|'), [gears]);

  useEffect(() => {
    spinAllowedRef.current = !deferSpinUntilCheck;
    snapGearAngles();
  }, [gearsSig, deferSpinUntilCheck, snapGearAngles]);

  useEffect(() => {
    let raf;
    const tick = (t) => {
      if (!lastTickRef.current) lastTickRef.current = t;
      const dt = clamp((t - lastTickRef.current) / 1000, 0, 0.08);
      lastTickRef.current = t;
      for (const g of gears) {
        const w = (spinAllowedRef.current ? omega.get(g.id) : 0) || 0;
        const el = gearRefs.current[g.id];
        if (!el) continue;
        const cur = Number(el.dataset.angle || 0);
        const next = cur + w * 90 * dt;
        el.dataset.angle = String(next);
        el.style.transform = `rotate(${next.toFixed(2)}deg)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      lastTickRef.current = 0;
      cancelAnimationFrame(raf);
    };
  }, [gears, omega]);

  const revealAnswer = useCallback(() => {
    if (completed) return;
    noteUserAction();
    spinAllowedRef.current = true;
    if (questionMode === 'fastest' || questionMode === 'slowest') {
      setPickedGearId(typeof content.answer === 'string' ? content.answer : null);
    } else if (questionMode === 'turn_count') {
      const t = Number(content.answerTurns);
      if (Number.isFinite(t)) setPickedTurns(t);
    }
    setFeedback(null);
  }, [completed, noteUserAction, questionMode, content.answer, content.answerTurns]);

  const checkAnswer = useCallback(() => {
    if (completed) return;
    if (deferSpinUntilCheck) spinAllowedRef.current = true;
    noteUserAction();
    let ok = false;
    if (questionMode === 'fastest') {
      ok = pickedGearId && pickedGearId === content.answer;
    } else if (questionMode === 'slowest') {
      ok = pickedGearId && pickedGearId === content.answer;
    } else if (questionMode === 'turn_count') {
      const target = Number(content.answerTurns);
      ok = pickedTurns != null && Number.isFinite(target) && Math.abs(pickedTurns - target) < 0.001;
    }
    if (ok) {
      setCompleted(true);
      setFeedback('correct');
      noteCorrectAnswer();
      celebrate();
      setA11y('Nice prediction!');
      trackMiniAppEvent.interaction(activityId, 'correct');
      trackMiniAppEvent.completed(activityId);
      onComplete?.({ activityId, engine: 'gear_speed', perfect: true });
    } else {
      setFeedback('wrong');
      noteIncorrectAnswer();
      setA11y('Try another pick.');
      trackMiniAppEvent.interaction(activityId, 'incorrect');
    }
  }, [
    completed,
    deferSpinUntilCheck,
    noteUserAction,
    questionMode,
    pickedGearId,
    pickedTurns,
    content.answer,
    content.answerTurns,
    noteCorrectAnswer,
    celebrate,
    activityId,
    onComplete,
    noteIncorrectAnswer,
  ]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (completed || gears.length === 0) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    const checkAnswerDisabled =
      questionMode === 'turn_count' ? pickedTurns == null : pickedGearId == null;
    onRegisterLessonFooterActions({
      checkAnswer,
      checkAnswerDisabled,
      showAnswer: revealAnswer,
    });
    return () => onRegisterLessonFooterActions(null);
  }, [
    onRegisterLessonFooterActions,
    completed,
    gears.length,
    checkAnswer,
    revealAnswer,
    questionMode,
    pickedGearId,
    pickedTurns,
  ]);

  const selectGear = useCallback(
    (id) => {
      if (completed) return;
      if (questionMode === 'turn_count') return;
      if (deferSpinUntilCheck && spinAllowedRef.current) {
        spinAllowedRef.current = false;
        snapGearAngles();
      }
      noteUserAction();
      setPickedGearId(id);
      setFeedback(null);
      onProgress?.({ activityId, engine: 'gear_speed', phase: 'pick', gearId: id });
    },
    [completed, questionMode, deferSpinUntilCheck, snapGearAngles, noteUserAction, activityId, onProgress],
  );

  const tryAgain = useCallback(() => {
    if (completed) return;
    if (deferSpinUntilCheck) {
      spinAllowedRef.current = false;
      snapGearAngles();
    }
    noteUserAction();
    setPickedGearId(null);
    setPickedTurns(null);
    setFeedback(null);
  }, [completed, deferSpinUntilCheck, snapGearAngles, noteUserAction]);

  const turnChoices = Array.isArray(content.turnChoices) && content.turnChoices.length
    ? content.turnChoices
    : [1, 2, 3, 4];

  const anyExplicitTeeth = useMemo(() => gears.some((g) => explicitTeeth(g) != null), [gears]);
  const showTeethLabels = content.showTeethLabels !== false && anyExplicitTeeth;

  const teethSummaryLine = useMemo(() => {
    if (content.showTeethSummary === false) return '';
    if (questionMode !== 'turn_count') return '';
    const parts = gears.map((g) => {
      const t = explicitTeeth(g);
      if (t == null) return null;
      return `${g.label || g.id}: ${t} teeth`;
    }).filter(Boolean);
    return parts.length >= 2 ? parts.join(' · ') : '';
  }, [gears, questionMode, content.showTeethSummary]);

  const prompt =
    content.prompt ||
    (questionMode === 'fastest'
      ? 'Which gear spins the fastest? Tap a gear, then Check.'
      : questionMode === 'slowest'
        ? 'Which gear spins the slowest? Tap a gear, then Check.'
        : `When ${content.turnCount?.referenceGearId || rootId} turns ${content.turnCount?.referenceTurns || 1} full time, how many turns does ${content.turnCount?.targetGearId || '?'} make?`);

  return (
    <div
      style={{
        ...getEngineRootStyle({ singleSurface }),
        position: 'relative',
        ...(guided.contentReserveStyle || {}),
        ...(compactGear ? { minHeight: 0, display: 'flex', flexDirection: 'column', flex: 1, gap: 8 } : {}),
      }}
    >
      <CelebrationOverlay visible={showCelebration} message={content.victory?.message || 'Great thinking!'} />
      <A11yAnnouncement message={a11y} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {!suppressActivityTitle ? (
        <h2 style={{ ...titleStyle, margin: compactGear ? '0 0 4px' : titleStyle.margin }}>{title}</h2>
      ) : null}
      {compactGear ? (
        <p
          style={{
            ...instructionsStyle,
            fontSize: narrowGear ? 13 : 14,
            lineHeight: 1.35,
            margin: 0,
            fontWeight: 600,
          }}
        >
          {prompt}
        </p>
      ) : (
        <>
          {content.instructions && <p style={instructionsStyle}>{content.instructions}</p>}
          <p style={{ ...instructionsStyle, fontWeight: 700, marginTop: content.instructions ? undefined : 0 }}>
            {prompt}
          </p>
        </>
      )}
      {teethSummaryLine ? (
        <p
          role="note"
          style={{
            ...instructionsStyle,
            marginTop: compactGear ? 2 : 4,
            marginBottom: 0,
            fontSize: narrowGear ? 11 : 13,
            fontWeight: 700,
            lineHeight: 1.3,
            padding: compactGear ? '6px 8px' : '8px 12px',
            borderRadius: compactGear ? 10 : 12,
            background: 'rgba(99,102,241,0.1)',
            border: '1px solid rgba(99,102,241,0.28)',
          }}
        >
          {teethSummaryLine}
        </p>
      ) : null}

      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${sceneW} / ${sceneH}`,
          borderRadius: compactGear ? 12 : 16,
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          marginBottom: compactGear ? 0 : 12,
          overflow: 'hidden',
        }}
      >
        <svg
          viewBox={`0 0 ${sceneW} ${sceneH}`}
          width="100%"
          height="100%"
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
          aria-hidden
        >
          {(connections || []).map((e, i) => {
            const a = gears.find((g) => g.id === e.from);
            const b = gears.find((g) => g.id === e.to);
            const sw = compactGear ? 2 : 2;
            if (!a || !b) return null;
            return (
              <line
                key={`${e.from}-${e.to}-${i}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke="var(--color-border)"
                strokeWidth={sw}
                strokeLinecap="round"
                opacity={0.75}
              />
            );
          })}
        </svg>
        {gears.map((g) => {
          const r = gearRadius(g);
          const size = r * 2;
          const teeth = g.teeth != null ? Number(g.teeth) : Math.max(8, Math.round(r / 2.5));
          const teethLabel = explicitTeeth(g);
          const active = pickedGearId === g.id;
          const isRoot = g.id === rootId;
          const bw = compactGear ? 2 : 3;
          return (
            <button
              key={g.id}
              type="button"
              disabled={completed || questionMode === 'turn_count'}
              onClick={() => selectGear(g.id)}
              aria-pressed={active}
              style={{
                position: 'absolute',
                left: `${(g.x / sceneW) * 100}%`,
                top: `${(g.y / sceneH) * 100}%`,
                transform: 'translate(-50%, -50%)',
                width: size + (compactGear ? 12 : 16),
                minWidth: compactGear ? 44 : 52,
                height: 'auto',
                minHeight: size + (compactGear ? 6 : 8),
                padding: compactGear ? '4px 4px 6px' : '6px 6px 8px',
                borderRadius: compactGear ? 12 : 16,
                border: `${bw}px solid ${
                  active ? '#22c55e' : isRoot ? 'var(--color-primary)' : 'var(--color-border)'
                }`,
                background: active ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
                cursor: completed || questionMode === 'turn_count' ? 'default' : 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                flexShrink: 0,
              }}
            >
              <div
                ref={(el) => {
                  gearRefs.current[g.id] = el;
                }}
                data-angle="0"
                style={{ width: size, maxWidth: '100%', flexShrink: 0 }}
              >
                <GearSvg teeth={teeth} size={size} color={isRoot ? 'var(--color-primary)' : '#64748b'} />
              </div>
              <span
                style={{
                  fontSize: narrowGear ? 9 : compactGear ? 9 : 10,
                  fontWeight: 800,
                  color: 'var(--color-text)',
                  textAlign: 'center',
                  lineHeight: 1.15,
                  maxWidth: '100%',
                  marginTop: 2,
                }}
              >
                {g.label || g.id}
              </span>
              {showTeethLabels && teethLabel != null ? (
                <span
                  style={{
                    fontSize: narrowGear ? 9 : compactGear ? 9 : 10,
                    fontWeight: 800,
                    letterSpacing: '0.02em',
                    color: 'var(--color-text)',
                    opacity: 0.88,
                    marginTop: 1,
                    textAlign: 'center',
                    lineHeight: 1.15,
                    maxWidth: '100%',
                  }}
                >
                  {teethLabel} teeth
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {questionMode === 'turn_count' ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: compactGear ? 6 : 8,
            marginBottom: compactGear ? 6 : 10,
          }}
        >
          {turnChoices.map((n) => (
            <button
              key={n}
              type="button"
              disabled={completed}
              onClick={() => {
                noteUserAction();
                setPickedTurns(n);
                setFeedback(null);
              }}
              style={{
                ...secondaryBtn,
                flex: '1 1 72px',
                minHeight: compactGear ? 44 : 48,
                fontSize: compactGear ? 14 : 16,
                borderWidth: pickedTurns === n ? 3 : 2,
                borderColor: pickedTurns === n ? 'var(--color-primary)' : 'var(--color-border)',
              }}
            >
              {n}
            </button>
          ))}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: compactGear ? 6 : 8 }}>
        {!shellLesson ? (
          <button
            type="button"
            style={{ ...primaryBtn, flex: '1 1 140px', minHeight: compactGear ? 44 : 48 }}
            onClick={checkAnswer}
            disabled={completed}
          >
            Check
          </button>
        ) : null}
        <button
          type="button"
          style={{
            ...secondaryBtn,
            flex: shellLesson ? '1 1 100%' : '1 1 120px',
            width: shellLesson ? '100%' : undefined,
            minHeight: compactGear ? 44 : 48,
            fontSize: compactGear ? 14 : 16,
          }}
          onClick={tryAgain}
          disabled={completed}
        >
          Reset pick
        </button>
      </div>

      {feedback === 'wrong' && !completed ? (
        <div
          style={{
            ...feedbackIncorrect,
            ...(compactGear
              ? {
                  background: '#FAEEDA',
                  borderRadius: 'var(--border-radius-md, 10px)',
                  padding: '8px 12px',
                  fontSize: 12,
                  lineHeight: 1.4,
                  marginTop: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                }
              : { marginTop: 10 }),
          }}
        >
          Try again — smaller meshed gears take more turns for the same belt speed.
        </div>
      ) : null}
      {completed ? (
        <div
          style={{
            ...feedbackCorrect,
            ...(compactGear
              ? {
                  background: '#EAF3DE',
                  borderRadius: 'var(--border-radius-md, 10px)',
                  padding: '8px 12px',
                  fontSize: 12,
                  marginTop: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                }
              : { marginTop: 10 }),
          }}
        >
          {content.victory?.message || 'Exactly — tooth size trades spin speed!'}
        </div>
      ) : null}
    </div>
  );
}
